import * as Sentry from '@sentry/react';

/**
 * Clerk Frontend API (FAPI) 422 monitor.
 *
 * Clerk's prebuilt components (`<SignIn/>`, `<SignUp/>`, …) handle validation
 * errors (HTTP 422) internally and surface them inline in the form — they are
 * never re-thrown, so they never reach Sentry through the global error
 * handlers. This module installs a thin `window.fetch` wrapper that watches
 * responses going to the Clerk FAPI host and reports every 422 to Sentry.
 *
 * It only inspects requests whose host matches the Clerk FAPI (derived from the
 * publishable key, exactly like clerk-js does); every other request — including
 * our own backend calls made through `ky` — is passed through untouched.
 */

interface ClerkErrorSummary {
  codes: string[];
  paramNames: string[];
}

interface ClerkFapi422Info extends ClerkErrorSummary {
  path: string;
}

/**
 * Decodes the Frontend API host from a Clerk publishable key.
 * Format: `pk_(test|live)_<base64(`<host>$`)>`.
 */
export function resolveClerkFapiHost(publishableKey: string): string | null {
  const match = /^pk_(?:test|live)_(.+)$/.exec(publishableKey.trim());
  if (!match) {
    return null;
  }
  try {
    const normalized = match[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
    const host = atob(padded).replace(/\$+$/, '');
    return host.length > 0 ? host : null;
  } catch {
    return null;
  }
}

interface ClerkErrorEntry {
  code?: unknown;
  meta?: { param_name?: unknown };
}

/**
 * Extracts the stable, non-PII identifiers from a Clerk error body
 * (`{ errors: [{ code, meta: { param_name } }] }`). Free-text messages are
 * intentionally dropped — they can echo user input.
 */
export function extractClerkErrorSummary(body: unknown): ClerkErrorSummary {
  const codes: string[] = [];
  const paramNames: string[] = [];
  if (!body || typeof body !== 'object' || !Array.isArray((body as { errors?: unknown }).errors)) {
    return { codes, paramNames };
  }
  for (const entry of (body as { errors: ClerkErrorEntry[] }).errors) {
    if (entry && typeof entry.code === 'string') {
      codes.push(entry.code);
    }
    const paramName = entry?.meta?.param_name;
    if (typeof paramName === 'string') {
      paramNames.push(paramName);
    }
  }
  return { codes, paramNames };
}

function requestUrl(input: RequestInfo | URL): string | null {
  if (typeof input === 'string') {
    return input;
  }
  if (input instanceof URL) {
    return input.href;
  }
  if (input instanceof Request) {
    return input.url;
  }
  return null;
}

function isClerkFapiRequest(input: RequestInfo | URL, fapiHost: string): boolean {
  const url = requestUrl(input);
  if (!url) {
    return false;
  }
  try {
    return new URL(url).host === fapiHost;
  } catch {
    return false;
  }
}

function safePath(input: RequestInfo | URL): string {
  const url = requestUrl(input);
  if (!url) {
    return '';
  }
  try {
    return new URL(url).pathname;
  } catch {
    return '';
  }
}

interface ClerkFapiFetchOptions {
  fapiHost: string;
  onClerk422: (info: ClerkFapi422Info) => void;
}

/**
 * Wraps a `fetch` implementation so that 422 responses from the Clerk FAPI host
 * are reported via `onClerk422`. Monitoring failures never affect the request:
 * the original (untouched) response is always returned.
 */
export function createClerkFapiFetch(
  baseFetch: typeof fetch,
  { fapiHost, onClerk422 }: ClerkFapiFetchOptions,
): typeof fetch {
  const wrapped = async (
    input: Parameters<typeof fetch>[0],
    init?: Parameters<typeof fetch>[1],
  ): Promise<Response> => {
    const response = await baseFetch(input, init);
    if (response.status === 422 && isClerkFapiRequest(input, fapiHost)) {
      try {
        const body = await response.clone().json();
        const summary = extractClerkErrorSummary(body);
        onClerk422({ path: safePath(input), ...summary });
      } catch {
        // The monitor must never interfere with the actual request flow.
      }
    }
    return response;
  };
  // `typeof fetch` carries the static `preconnect` member in Bun's lib types;
  // forward it from the wrapped implementation so the wrapper stays a drop-in.
  return Object.assign(wrapped, { preconnect: baseFetch.preconnect });
}

interface InstallClerkFapiMonitorDeps {
  publishableKey: string;
  captureMessage?: typeof Sentry.captureMessage;
}

/**
 * Installs the FAPI 422 monitor on `window.fetch`. Returns a cleanup function
 * that restores the original `fetch`. No-op (returns a noop cleanup) when the
 * publishable key cannot be decoded or there is no `window`.
 */
export function installClerkFapiMonitor(deps: InstallClerkFapiMonitorDeps): () => void {
  const fapiHost = resolveClerkFapiHost(deps.publishableKey);
  if (!fapiHost || typeof window === 'undefined') {
    return () => {
      // nothing to restore — the monitor was never installed
    };
  }
  const captureMessage = deps.captureMessage ?? Sentry.captureMessage;
  const originalFetch = window.fetch;

  window.fetch = createClerkFapiFetch(originalFetch, {
    fapiHost,
    onClerk422: ({ path, codes, paramNames }) => {
      captureMessage('Clerk FAPI 422', {
        // Clerk 422s are expected validation outcomes, not JS crashes — keep
        // them as warnings so they don't pollute the error dashboard.
        level: 'warning',
        tags: { source: 'clerk-fapi', status: '422' },
        fingerprint: ['clerk-fapi-422', ...codes],
        extra: { path, clerkErrorCodes: codes, clerkParamNames: paramNames },
      });
    },
  });

  return () => {
    window.fetch = originalFetch;
  };
}
